import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Avatar,
  Chip,
  Rating,
  Container,
  CircularProgress,
  Pagination,
  Stack,
  Paper,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupIcon from '@mui/icons-material/Group';
import WorkIcon from '@mui/icons-material/Work';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { useDispatch, useSelector } from "react-redux";
import { NavHashLink } from "react-router-hash-link";
import { useAuthToken } from "../TokenContext";
import { useIntl } from "react-intl";
import axios from "axios";

// Styled components remain the same as before
// Set up common Artemy styles for components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--artemy-bg-card)',
  border: '1px solid var(--artemy-border)',
  borderRadius: '14px',
  boxShadow: 'none',
  backgroundImage: 'none',
  transition: 'transform 0.2s, border-color 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: 'var(--artemy-border-bright)',
  },
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  marginBottom: '32px',
  display: 'flex',
  width: '100%',
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  margin: theme.spacing(1),
  minWidth: 200,
  '& label': {
    color: 'var(--artemy-text-secondary)',
  },
  '& label.Mui-focused': {
    color: 'var(--artemy-primary)',
  },
  '& .MuiOutlinedInput-root': {
    color: 'var(--artemy-text-primary)',
    '& fieldset': {
      borderColor: 'var(--artemy-border)',
    },
    '&:hover fieldset': {
      borderColor: 'var(--artemy-primary)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--artemy-primary)',
    },
    '& .MuiSvgIcon-root': {
      color: 'var(--artemy-text-secondary)',
    }
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: '10px 24px',
  borderRadius: '8px',
  textTransform: 'none',
  fontFamily: 'Outfit, sans-serif',
  fontWeight: 600,
  backgroundColor: 'var(--artemy-primary)',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: 'var(--artemy-primary-hover)',
  }
}));

const FindPodsClient = ({ collapsed }) => {
  const token = useAuthToken();
  const intl = useIntl();
  const { isLoggedIn, user } = useSelector((state) => state.auth);

  // State management
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [subscription, setSubscription] = useState({});
  const [getData, setGetData] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    podType: '',
    timezone: '',
    podSize: '',
    location: '',
  });

  // Fetch initial pods data
  const fetchInitialPods = async () => {
    if (!token || !user?.data?.[0]) return;

    try {
      const body = {
        emp_id: user.data[0].emp_id,
        Client_Code: user.data[0].client_code,
        podlocation: '',
        podtype: '',
        podSize: '',
        timezone: '',
        manager_type: user.data[0].manager_type,
      };

      const response = await axios.post(
        process.env.REACT_APP_CLIENT_CLIENTFINDPODS,
        body,
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );
      setGetData(response.data.data);
    } catch (error) {
      console.error("Error fetching initial pods:", error);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      if (!token || !user?.data?.[0]?.client_code) return;

      try {
        // Fetch subscription data
        const body = {
          client_code: user.data[0].client_code,
        };

        const headers = {
          Authorization: token,
          "Content-Type": "application/json",
        };

        const endpoint = user.data[0].client_code.includes("PARTNER")
          ? process.env.REACT_APP_PARTNER_FTECHSUBSCCRIPTIONPARTNER
          : process.env.REACT_APP_FETCHSUBSCRIPTIONDATA;

        const response = await axios.post(endpoint, body, { headers });
        setSubscription(response.data.data);

        // Fetch initial pods
        await fetchInitialPods();
      } catch (error) {
        console.error("Error initializing data:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    initializeData();
  }, [token]);

  const handleFilterChange = (event) => {
    setFilters({
      ...filters,
      [event.target.name]: event.target.value,
    });
  };

  const applyFilters = async () => {
    setFilterLoading(true);
    const body = {
      emp_id: user.data[0].emp_id,
      Client_Code: user.data[0].client_code,
      podlocation: filters.location,
      podtype: filters.podType,
      podSize: filters.podSize,
      timezone: filters.timezone,
      manager_type: user.data[0].manager_type,
    };

    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_CLIENTFINDPODS,
        body,
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );
      setGetData(response.data.data);
    } catch (error) {
      console.error("Error applying filters:", error);
    } finally {
      setFilterLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = getData.slice(indexOfFirstItem, indexOfLastItem);

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (subscription === "There Are No subscriction data.") {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography variant="h5">
          {intl.formatMessage({ id: "noSubscription" })}
        </Typography>
      </Box>
    );
  }

  return (
    <div className="artemy-fade-up">
      <Box sx={{ py: 4, position: 'relative', zIndex: 1, width: '100%' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 className="artemy-page-title">
            {intl.formatMessage({ id: "findBestPod" })}
          </h1>
          <p className="artemy-page-sub">
            Discover external and internal PODs matching your criteria
          </p>
        </div>

        <FilterContainer>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <StyledFormControl fullWidth>
                <InputLabel>{intl.formatMessage({ id: "timeZone" })}</InputLabel>
                <Select
                  name="timezone"
                  value={filters.timezone}
                  onChange={handleFilterChange}
                  label="Time Zone"
                >
                  <MenuItem value="">   {intl.formatMessage({ id: "all" })}</MenuItem>
                  <MenuItem value="indian_standard_time">Indian Standard Time</MenuItem>
                  <MenuItem value="eastern_standard_time">Eastern Standard Time</MenuItem>
                  <MenuItem value="central_standard_time">Central Standard Time</MenuItem>
                  <MenuItem value="mountain_standard_time">Mountain Standard Time</MenuItem>
                  <MenuItem value="pacific_standard_time">Pacific Standard Time</MenuItem>
                  <MenuItem value="greenwich_mean_time">Greenwich Mean Time</MenuItem>
                  <MenuItem value="central_european_time">Central European Time</MenuItem>
                  <MenuItem value="western_european_time">Western European Time</MenuItem>
                  <MenuItem value="australian_eastern_standard_time">Australian Eastern Standard Time</MenuItem>
                  <MenuItem value="australian_central_standard_time">Australian Central Standard Time</MenuItem>
                  <MenuItem value="australian_western_standard_time">Australian Western Standard Time</MenuItem>
                  <MenuItem value="new_york_time">New York Time (Eastern Daylight Time)</MenuItem>
                  <MenuItem value="los_angeles_time">Los Angeles Time (Pacific Daylight Time)</MenuItem>
                  <MenuItem value="london_time">London Time (British Summer Time)</MenuItem>
                  <MenuItem value="paris_time">Paris Time (Central European Summer Time)</MenuItem>
                  <MenuItem value="tokyo_time">Tokyo Time (Japan Standard Time)</MenuItem>
                  <MenuItem value="sydney_time">Sydney Time (Australian Eastern Daylight Time)</MenuItem>
                  <MenuItem value="dubai_time">Dubai Time (Gulf Standard Time)</MenuItem>
                  <MenuItem value="moscow_time">Moscow Time (Moscow Standard Time)</MenuItem>
                  <MenuItem value="brazil_time">Brazil Time (Brasilia Time)</MenuItem>
                  <MenuItem value="chicago_time">Chicago Time (Central Daylight Time)</MenuItem>
                  <MenuItem value="houston_time">Houston Time (Central Daylight Time)</MenuItem>
                  <MenuItem value="cape_town_time">Cape Town Time (South Africa Standard Time)</MenuItem>
                </Select>
              </StyledFormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StyledFormControl fullWidth>
                <InputLabel>   {intl.formatMessage({ id: "podSize" })}</InputLabel>
                <Select
                  name="podSize"
                  value={filters.podSize}
                  onChange={handleFilterChange}
                  label="Pod Size"
                >
                  <MenuItem value="">   {intl.formatMessage({ id: "all" })}</MenuItem>
                  <MenuItem value="2">2</MenuItem>
                  <MenuItem value="3">3</MenuItem>

                  <MenuItem value="5">5</MenuItem>
                  <MenuItem value="7">7</MenuItem>
                </Select>
              </StyledFormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StyledFormControl fullWidth>
                <InputLabel>   {intl.formatMessage({ id: "podType" })}</InputLabel>
                <Select
                  name="podType"
                  value={filters.podType}
                  onChange={handleFilterChange}
                  label="Pod Type"
                >
                  <MenuItem value="">   {intl.formatMessage({ id: "all" })}</MenuItem>
                  <MenuItem value="Saas">SaaS</MenuItem>
                  <MenuItem value="Web app">Web App</MenuItem>
                  <MenuItem value="Java Developer">Java Developer</MenuItem>
                </Select>
              </StyledFormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StyledFormControl fullWidth>
                <InputLabel> {intl.formatMessage({ id: "location" })}</InputLabel>
                <Select
                  name="location"
                  value={filters.location}
                  onChange={handleFilterChange}
                  label={intl.formatMessage({ id: "location" })}
                >
                  <MenuItem value="">   {intl.formatMessage({ id: "all" })}</MenuItem>
                  <MenuItem value="usa">USA</MenuItem>
                  <MenuItem value="india">India</MenuItem>
                  <MenuItem value="canada">Canada</MenuItem>
                  <MenuItem value="australia">Australia</MenuItem>
                  <MenuItem value="uk">United Kingdom</MenuItem>
                  <MenuItem value="germany">Germany</MenuItem>
                  <MenuItem value="france">France</MenuItem>
                  <MenuItem value="china">China</MenuItem>
                  <MenuItem value="japan">Japan</MenuItem>
                  <MenuItem value="brazil">Brazil</MenuItem>
                  <MenuItem value="south_africa">South Africa</MenuItem>
                  <MenuItem value="mexico">Mexico</MenuItem>
                  <MenuItem value="italy">Italy</MenuItem>
                  <MenuItem value="spain">Spain</MenuItem>
                  <MenuItem value="russia">Russia</MenuItem>
                  <MenuItem value="south_korea">South Korea</MenuItem>
                  <MenuItem value="new_zealand">New Zealand</MenuItem>
                  <MenuItem value="singapore">Singapore</MenuItem>
                  <MenuItem value="uae">United Arab Emirates</MenuItem>
                  <MenuItem value="saudi_arabia">Saudi Arabia</MenuItem>
                  <MenuItem value="argentina">Argentina</MenuItem>
                  <MenuItem value="netherlands">Netherlands</MenuItem>
                  <MenuItem value="sweden">Sweden</MenuItem>
                  <MenuItem value="switzerland">Switzerland</MenuItem>
                  <MenuItem value="norway">Norway</MenuItem>
                  <MenuItem value="denmark">Denmark</MenuItem>
                  <MenuItem value="finland">Finland</MenuItem>
                  <MenuItem value="portugal">Portugal</MenuItem>
                  <MenuItem value="poland">Poland</MenuItem>
                  <MenuItem value="turkey">Turkey</MenuItem>
                  <MenuItem value="indonesia">Indonesia</MenuItem>
                  <MenuItem value="malaysia">Malaysia</MenuItem>
                  <MenuItem value="thailand">Thailand</MenuItem>
                  <MenuItem value="vietnam">Vietnam</MenuItem>
                  <MenuItem value="philippines">Philippines</MenuItem>
                  <MenuItem value="egypt">Egypt</MenuItem>
                  <MenuItem value="nigeria">Nigeria</MenuItem>
                  <MenuItem value="kenya">Kenya</MenuItem>
                  <MenuItem value="ghana">Ghana</MenuItem>
                  <MenuItem value="colombia">Colombia</MenuItem>
                  <MenuItem value="chile">Chile</MenuItem>
                  <MenuItem value="peru">Peru</MenuItem>
                  <MenuItem value="venezuela">Venezuela</MenuItem>
                  <MenuItem value="pakistan">Pakistan</MenuItem>
                  <MenuItem value="bangladesh">Bangladesh</MenuItem>
                  <MenuItem value="sri_lanka">Sri Lanka</MenuItem>
                  <MenuItem value="nepal">Nepal</MenuItem>
                  <MenuItem value="bhutan">Bhutan</MenuItem>
                  <MenuItem value="maldives">Maldives</MenuItem>
                  <MenuItem value="iceland">Iceland</MenuItem>
                  <MenuItem value="luxembourg">Luxembourg</MenuItem>
                  <MenuItem value="qatar">Qatar</MenuItem>
                  <MenuItem value="oman">Oman</MenuItem>
                  <MenuItem value="kuwait">Kuwait</MenuItem>
                  <MenuItem value="bahrain">Bahrain</MenuItem>
                  <MenuItem value="iraq">Iraq</MenuItem>
                  <MenuItem value="iran">Iran</MenuItem>
                  <MenuItem value="afghanistan">Afghanistan</MenuItem>
                  <MenuItem value="israel">Israel</MenuItem>
                  <MenuItem value="jordan">Jordan</MenuItem>
                  <MenuItem value="lebanon">Lebanon</MenuItem>
                  <MenuItem value="morocco">Morocco</MenuItem>
                  <MenuItem value="algeria">Algeria</MenuItem>
                  <MenuItem value="tunisia">Tunisia</MenuItem>
                  <MenuItem value="libya">Libya</MenuItem>
                  <MenuItem value="ethiopia">Ethiopia</MenuItem>
                  <MenuItem value="tanzania">Tanzania</MenuItem>
                  <MenuItem value="uganda">Uganda</MenuItem>
                  <MenuItem value="zimbabwe">Zimbabwe</MenuItem>
                  <MenuItem value="zambia">Zambia</MenuItem>
                  <MenuItem value="mozambique">Mozambique</MenuItem>
                  <MenuItem value="botswana">Botswana</MenuItem>
                  <MenuItem value="namibia">Namibia</MenuItem>
                  <MenuItem value="angola">Angola</MenuItem>
                  <MenuItem value="cuba">Cuba</MenuItem>
                  <MenuItem value="jamaica">Jamaica</MenuItem>
                  <MenuItem value="haiti">Haiti</MenuItem>
                  <MenuItem value="dominican_republic">Dominican Republic</MenuItem>
                  <MenuItem value="trinidad_tobago">Trinidad and Tobago</MenuItem>
                  <MenuItem value="bahamas">Bahamas</MenuItem>
                  <MenuItem value="barbados">Barbados</MenuItem>
                  <MenuItem value="panama">Panama</MenuItem>
                  <MenuItem value="costa_rica">Costa Rica</MenuItem>
                  <MenuItem value="honduras">Honduras</MenuItem>
                  <MenuItem value="guatemala">Guatemala</MenuItem>
                  <MenuItem value="el_salvador">El Salvador</MenuItem>
                  <MenuItem value="nicaragua">Nicaragua</MenuItem>
                  <MenuItem value="belize">Belize</MenuItem>
                  <MenuItem value="paraguay">Paraguay</MenuItem>
                  <MenuItem value="uruguay">Uruguay</MenuItem>
                  <MenuItem value="bolivia">Bolivia</MenuItem>
                  <MenuItem value="ecuador">Ecuador</MenuItem>
                  <MenuItem value="guinea">Guinea</MenuItem>
                  <MenuItem value="ivory_coast">Ivory Coast</MenuItem>
                  <MenuItem value="cameroon">Cameroon</MenuItem>
                  <MenuItem value="senegal">Senegal</MenuItem>
                  <MenuItem value="sudan">Sudan</MenuItem>
                  <MenuItem value="south_sudan">South Sudan</MenuItem>
                  <MenuItem value="congo">Congo</MenuItem>
                  <MenuItem value="madagascar">Madagascar</MenuItem>
                  <MenuItem value="malawi">Malawi</MenuItem>
                  <MenuItem value="sierra_leone">Sierra Leone</MenuItem>
                  <MenuItem value="chad">Chad</MenuItem>
                  <MenuItem value="mali">Mali</MenuItem>
                  <MenuItem value="burkina_faso">Burkina Faso</MenuItem>
                  <MenuItem value="rwanda">Rwanda</MenuItem>
                  <MenuItem value="burundi">Burundi</MenuItem>
                  <MenuItem value="lesotho">Lesotho</MenuItem>
                  <MenuItem value="eswatini">Eswatini</MenuItem>

                </Select>
              </StyledFormControl>
            </Grid>

            <Grid item xs={12} display="flex" justifyContent="center">
              <StyledButton
                variant="contained"
                color="primary"
                onClick={applyFilters}
                startIcon={<WorkIcon />}
                disabled={filterLoading}
              >
                {filterLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  intl.formatMessage({ id: "applyFilters" })
                )}
              </StyledButton>
            </Grid>
          </Grid>
        </FilterContainer>

        {filterLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {currentItems.length > 0 ? (
                currentItems.map((pod) => (
                  <Grid item xs={12} sm={6} md={4} key={pod.id}>
                    <StyledCard>
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={2}>
                          <Avatar
                            src={pod.picture || '/defaultimg.png'}
                            sx={{ width: 56, height: 56, mr: 2 }}
                          />
                          <Box>
                            <Typography variant="h6">{pod.podName}</Typography>
                            <Rating value={Number(pod.avgrating) || 0} readOnly precision={0.5} />
                          </Box>
                        </Box>

                        <Chip
                          label={pod.Client_Code === user.data[0].Client_Code ? "Internal Pod" : "External Pod"}
                          color={pod.Client_Code === user.data[0].Client_Code ? "primary" : "error"}
                          sx={{ mb: 2 }}
                        />

                        <Divider sx={{ my: 2, borderColor: 'var(--artemy-border)' }} />

                        <Stack spacing={1}>
                          <Box display="flex" alignItems="center">
                            <LocationOnIcon sx={{ mr: 1, color: 'var(--artemy-accent)' }} />
                            <Typography sx={{ color: 'var(--artemy-text-secondary)' }}>{pod.location}</Typography>
                          </Box>

                          <Box display="flex" alignItems="center">
                            <GroupIcon sx={{ mr: 1, color: 'var(--artemy-accent)' }} />
                            <Typography sx={{ color: 'var(--artemy-text-secondary)' }}>Size: {pod.podsize}</Typography>
                          </Box>

                          <Box display="flex" alignItems="center">
                            <MonetizationOnIcon sx={{ mr: 1, color: 'var(--artemy-accent)' }} />
                            <Typography sx={{ color: 'var(--artemy-text-secondary)' }}>
                              Rate: {user.data[0].manager_type !== "PartnerClient"
                                ? pod.pototalrate
                                : pod.pod_rate}
                            </Typography>
                          </Box>

                          <Box display="flex" alignItems="center">
                            <WorkIcon sx={{ mr: 1, color: 'var(--artemy-accent)' }} />
                            <Typography sx={{ color: 'var(--artemy-text-secondary)' }}>   {intl.formatMessage({ id: "podType" })}: {pod.podtype}</Typography>
                          </Box>
                        </Stack>

                        <Button
                          component={NavHashLink}
                          to={`/FindPodDetailsClient/${pod.id}`}
                          variant="contained"
                          color="primary"
                          fullWidth
                          sx={{ mt: 3 }}
                        >
                          {intl.formatMessage({ id: "viewPodDetails" })}
                        </Button>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography variant="h6" align="center">
                    {intl.formatMessage({ id: "noPodsMatchingCriteria" })}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {getData.length > itemsPerPage && (
              <Box display="flex" justifyContent="center" mt={4}>
                <Pagination
                  count={Math.ceil(getData.length / itemsPerPage)}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </div>
  );
};

export default FindPodsClient;